import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { 
  UserCheck, 
  UserX,
  Clock,
  Mail,
  Phone,
  Users,
  CheckCircle,
  XCircle,
  AlertTriangle,
  RefreshCw,
  Sparkles,
  CreditCard,
  Crown,
  Briefcase,
  Eye,
  TrendingUp,
  DollarSign,
  UserPlus,
  X,
  Globe,
  Archive,
  RotateCcw,
  Trash2,
  Calendar,
  AlertCircle,
  FileText,
  Link2,
  Copy
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import DashboardLayout from "../../components/DashboardLayout";
import ConvertTrialUserModal from "../../components/Admin/ConvertTrialUserModal";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COUNTRIES = [
  "México", "Colombia", "Argentina", "Perú", "Chile", "Ecuador", "Venezuela", 
  "Guatemala", "Honduras", "El Salvador", "Nicaragua", "Costa Rica", "Panamá", 
  "República Dominicana", "Brasil", "España", "Estados Unidos", "Otro"
];

const DECLINE_REASONS = [
  { value: "no_interesado", label: "No interesado" },
  { value: "datos_falsos", label: "Datos falsos/incorrectos" },
  { value: "spam", label: "Spam" },
  { value: "duplicado", label: "Cuenta duplicada" },
  { value: "otro", label: "Otro" }
];

// Decline Modal Component
const DeclineTrialModal = ({ isOpen, onClose, user, onDeclined }) => {
  const [reason, setReason] = useState("");
  const [observation, setObservation] = useState("");
  const [loading, setLoading] = useState(false);

  const handleDecline = async () => {
    if (!reason) {
      toast.error("Selecciona una razón");
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/admin/trials/${user.id}/decline`, {
        reason,
        observation: observation.trim() || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Usuario archivado correctamente");
      onDeclined && onDeclined();
      onClose();
      setReason("");
      setObservation("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al archivar usuario");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !user) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
        >
          {/* Header */}
          <div className="relative bg-gradient-to-r from-red-500 to-red-600 p-6 text-white">
            <button 
              onClick={onClose}
              className="absolute top-4 right-4 p-2 hover:bg-white/20 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                <Archive className="w-6 h-6" />
              </div>
              <div>
                <h2 className="text-xl font-bold">Rechazar y Archivar</h2>
                <p className="text-red-100 text-sm">El usuario será movido a archivados</p>
              </div>
            </div>
          </div>

          {/* User Info */}
          <div className="p-4 bg-red-50 border-b border-red-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center text-red-700 font-bold">
                {user.name?.charAt(0).toUpperCase() || "?"}
              </div>
              <div>
                <p className="font-semibold text-navy-900">{user.name}</p>
                <p className="text-sm text-navy-500">{user.email}</p>
              </div>
            </div>
          </div>

          {/* Form */}
          <div className="p-6 space-y-4">
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <AlertCircle className="w-4 h-4" /> Razón del rechazo *
              </Label>
              <Select value={reason} onValueChange={setReason} data-testid="decline-reason-select">
                <SelectTrigger data-testid="decline-reason-trigger">
                  <SelectValue placeholder="Selecciona una razón..." />
                </SelectTrigger>
                <SelectContent data-testid="decline-reason-content">
                  {DECLINE_REASONS.map((r) => (
                    <SelectItem key={r.value} value={r.value} data-testid={`decline-reason-${r.value}`}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <FileText className="w-4 h-4" /> Observación (opcional)
              </Label>
              <Textarea
                value={observation}
                onChange={(e) => setObservation(e.target.value)}
                placeholder="Notas adicionales sobre el rechazo..."
                className="h-24 resize-none"
                data-testid="decline-observation-input"
              />
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-sm text-amber-700">
                <strong>Nota:</strong> El usuario podrá ser restaurado desde la pestaña "Archivados" si cambias de opinión.
              </p>
            </div>

            <div className="flex gap-3 pt-2">
              <Button type="button" variant="outline" onClick={onClose} className="flex-1" disabled={loading} data-testid="decline-cancel-btn">
                Cancelar
              </Button>
              <Button 
                onClick={handleDecline} 
                className="flex-1 bg-red-600 hover:bg-red-700" 
                disabled={loading || !reason}
                data-testid="decline-submit-btn"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <Archive className="w-4 h-4 mr-2" />
                    Archivar
                  </>
                )}
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

// Create Vendor Trial Modal
const CreateVendorTrialModal = ({ isOpen, onClose, onCreated }) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    country: "México"
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [createdVendor, setCreatedVendor] = useState(null);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = "Nombre requerido";
    if (!formData.email.trim()) newErrors.email = "Email requerido";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = "Email inválido";
    if (!formData.phone.trim()) newErrors.phone = "Teléfono requerido";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(`${API}/auth/vendor-trial-account`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Show success with referral link info
      setCreatedVendor({
        name: formData.name,
        email: formData.email,
        referral_code: response.data.user?.referral_code
      });
      
      onCreated && onCreated(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al crear vendedor");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({ name: "", email: "", phone: "", country: "México" });
    setCreatedVendor(null);
    setErrors({});
    onClose();
  };

  const copyReferralLink = () => {
    if (createdVendor?.referral_code) {
      const url = `${window.location.origin}/r/${createdVendor.referral_code}`;
      navigator.clipboard.writeText(url);
      toast.success("Link copiado al portapapeles");
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
        >
          {/* Success View - After vendor created */}
          {createdVendor ? (
            <>
              <div className="relative bg-gradient-to-r from-emerald-600 to-teal-600 p-6 text-white">
                <button 
                  onClick={handleClose}
                  className="absolute top-4 right-4 p-2 hover:bg-white/20 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                    <CheckCircle className="w-6 h-6" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold">¡Vendedor Creado!</h2>
                    <p className="text-emerald-100 text-sm">{createdVendor.name}</p>
                  </div>
                </div>
              </div>

              <div className="p-6 space-y-4">
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-emerald-700 mb-2">
                    <Mail className="w-4 h-4" />
                    <span className="text-sm font-medium">Credenciales enviadas a:</span>
                  </div>
                  <p className="text-emerald-800 font-semibold">{createdVendor.email}</p>
                </div>

                {createdVendor.referral_code && (
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 text-purple-700 mb-2">
                      <Link2 className="w-4 h-4" />
                      <span className="text-sm font-medium">Link de Referido:</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={`${window.location.origin}/r/${createdVendor.referral_code}`}
                        readOnly
                        className="flex-1 bg-white border border-purple-200 rounded-lg px-3 py-2 text-sm text-purple-800"
                      />
                      <Button
                        size="sm"
                        onClick={copyReferralLink}
                        className="bg-purple-600 hover:bg-purple-700"
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                    <p className="text-xs text-purple-600 mt-2">
                      Este link permite al vendedor generar leads automáticamente
                    </p>
                  </div>
                )}

                <div className="bg-navy-50 border border-navy-200 rounded-lg p-3">
                  <p className="text-sm text-navy-700">
                    <strong>Próximos pasos:</strong>
                    <br />• El vendedor recibirá un email para activar su cuenta
                    <br />• Tendrá 14 días de prueba
                    <br />• Puede compartir su link desde "Mis Leads"
                  </p>
                </div>

                <Button onClick={handleClose} className="w-full bg-emerald-600 hover:bg-emerald-700">
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Cerrar
                </Button>
              </div>
            </>
          ) : (
            <>
              {/* Header */}
              <div className="relative bg-gradient-to-r from-purple-600 to-indigo-600 p-6 text-white">
                <button 
                  onClick={handleClose}
                  className="absolute top-4 right-4 p-2 hover:bg-white/20 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                    <UserPlus className="w-6 h-6" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold">Crear Vendedor Prueba</h2>
                    <p className="text-purple-100 text-sm">Cuenta demo por 14 días</p>
                  </div>
                </div>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="p-6 space-y-4">
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Users className="w-4 h-4" /> Nombre completo *
                  </Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Nombre del vendedor"
                    className={errors.name ? "border-red-500" : ""}
                  />
                  {errors.name && <p className="text-xs text-red-500">{errors.name}</p>}
                </div>

                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Mail className="w-4 h-4" /> Email *
                  </Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="vendedor@email.com"
                    className={errors.email ? "border-red-500" : ""}
                  />
                  {errors.email && <p className="text-xs text-red-500">{errors.email}</p>}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Phone className="w-4 h-4" /> Teléfono *
                    </Label>
                    <Input
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="+52 55 1234 5678"
                      className={errors.phone ? "border-red-500" : ""}
                    />
                    {errors.phone && <p className="text-xs text-red-500">{errors.phone}</p>}
                  </div>

                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Globe className="w-4 h-4" /> País
                    </Label>
                    <Select value={formData.country} onValueChange={(v) => setFormData({ ...formData, country: v })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {COUNTRIES.map((c) => (
                          <SelectItem key={c} value={c}>{c}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 mt-4">
                  <p className="text-sm text-purple-700">
                    <strong>Nota:</strong> El vendedor recibirá un email con sus credenciales y tendrá 14 días de prueba.
                  </p>
                </div>

                <div className="flex gap-3 pt-2">
                  <Button type="button" variant="outline" onClick={handleClose} className="flex-1" disabled={loading}>
                    Cancelar
                  </Button>
                  <Button type="submit" className="flex-1 bg-purple-600 hover:bg-purple-700" disabled={loading}>
                    {loading ? (
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <>
                        <UserPlus className="w-4 h-4 mr-2" />
                        Crear Vendedor
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </>
          )}
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

// Helper to calculate days left
const getDaysLeft = (expiresAt) => {
  if (!expiresAt) return null;
  const daysLeft = Math.ceil((new Date(expiresAt) - new Date()) / (1000 * 60 * 60 * 24));
  return daysLeft;
};

// Expiration Badge Component
const ExpirationBadge = ({ expiresAt, isVendor = false }) => {
  const daysLeft = getDaysLeft(expiresAt);
  
  if (daysLeft === null) return null;
  
  const isExpired = daysLeft <= 0;
  const isExpiringSoon = daysLeft > 0 && daysLeft <= (isVendor ? 3 : 2);
  const totalDays = isVendor ? 14 : 7;
  
  return (
    <span className={`px-2 py-1 text-xs font-medium rounded-full flex items-center gap-1 ${
      isExpired ? 'bg-red-100 text-red-700' : 
      isExpiringSoon ? 'bg-orange-100 text-orange-700' : 
      'bg-emerald-100 text-emerald-700'
    }`}>
      <Clock className="w-3 h-3" />
      {isExpired ? 'Expirado' : `${daysLeft}/${totalDays}d`}
    </span>
  );
};

// Trial Users Tab Component
const TrialUsersTab = ({ onRefresh }) => {
  const [trialClients, setTrialClients] = useState([]);
  const [trialVendors, setTrialVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [showCreateVendorModal, setShowCreateVendorModal] = useState(false);
  const [showDeclineModal, setShowDeclineModal] = useState(false);
  const [declineUser, setDeclineUser] = useState(null);

  useEffect(() => {
    fetchTrialUsers();
  }, []);

  const fetchTrialUsers = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const users = response.data;
      
      // Separate trial clients and trial vendors (exclude archived)
      const clients = users.filter(u => 
        u.role === "client" && 
        (u.plan_type === "prueba" || u.is_trial === true) &&
        u.status !== "archived"
      );
      const vendors = users.filter(u => 
        u.role === "vendedor" && 
        (u.vendor_level === "prueba" || u.is_trial === true) &&
        u.status !== "archived"
      );
      
      setTrialClients(clients);
      setTrialVendors(vendors);
    } catch (error) {
      toast.error("Error al cargar usuarios de prueba");
    } finally {
      setLoading(false);
    }
  };

  const handleConvertClient = (user) => {
    setSelectedUser(user);
    setShowConvertModal(true);
  };

  const handleDeclineUser = (user) => {
    setDeclineUser(user);
    setShowDeclineModal(true);
  };

  const handleConvertVendor = async (user) => {
    try {
      const token = localStorage.getItem("token");
      await axios.put(`${API}/admin/users/${user.id}`, {
        vendor_level: "basico",
        is_trial: false,
        plan_type: null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${user.name} ahora es un Agente Activo`);
      fetchTrialUsers();
      onRefresh && onRefresh();
    } catch (error) {
      toast.error("Error al activar vendedor");
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString('es-MX', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-10 h-10 border-4 border-purple-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
              <Users className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-amber-700">{trialClients.length}</p>
              <p className="text-sm text-amber-600">Clientes en Prueba (7 días)</p>
            </div>
          </div>
        </div>
        <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <Briefcase className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-purple-700">{trialVendors.length}</p>
                <p className="text-sm text-purple-600">Vendedores en Prueba (14 días)</p>
              </div>
            </div>
            <Button 
              size="sm"
              onClick={() => setShowCreateVendorModal(true)}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="create-vendor-trial-btn"
            >
              <UserPlus className="w-4 h-4 mr-1" />
              Nuevo
            </Button>
          </div>
        </div>
      </div>

      {/* Trial Clients Section */}
      <div>
        <h3 className="text-lg font-semibold text-navy-900 mb-3 flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-amber-500" />
          Clientes en Modo Prueba
          <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded-full">
            {trialClients.length}
          </span>
        </h3>
        
        {trialClients.length === 0 ? (
          <div className="bg-gray-50 rounded-xl p-8 text-center">
            <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No hay clientes en modo prueba</p>
          </div>
        ) : (
          <div className="space-y-3">
            {trialClients.slice(0, 10).map((client) => (
              <motion.div
                key={client.id}
                initial={{ opacity: 0, y: 5 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white border border-amber-100 rounded-xl p-4 hover:shadow-md transition-shadow"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center text-amber-700 font-bold">
                      {client.name?.charAt(0).toUpperCase() || "?"}
                    </div>
                    <div>
                      <h4 className="font-semibold text-navy-900">{client.name}</h4>
                      <div className="flex items-center gap-3 text-sm text-navy-500">
                        <span className="flex items-center gap-1">
                          <Mail className="w-3 h-3" /> {client.email}
                        </span>
                        {client.phone && (
                          <span className="flex items-center gap-1">
                            <Phone className="w-3 h-3" /> {client.phone}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-navy-400 mt-1">
                        Registrado: {formatDate(client.created_at)}
                        {client.country && ` • ${client.country}`}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <ExpirationBadge expiresAt={client.trial_expires_at} isVendor={false} />
                    <span className="px-2 py-1 bg-amber-100 text-amber-700 text-xs font-medium rounded-full">
                      Prueba
                    </span>
                    <Button 
                      size="sm"
                      variant="outline"
                      onClick={() => handleDeclineUser(client)}
                      className="border-red-200 text-red-600 hover:bg-red-50"
                      data-testid="decline-trial-btn"
                    >
                      <XCircle className="w-4 h-4" />
                    </Button>
                    <Button 
                      size="sm"
                      onClick={() => handleConvertClient(client)}
                      className="bg-emerald-600 hover:bg-emerald-700"
                      data-testid="convert-trial-btn"
                    >
                      <Crown className="w-4 h-4 mr-1" />
                      Convertir
                    </Button>
                  </div>
                </div>
              </motion.div>
            ))}
            {trialClients.length > 10 && (
              <p className="text-center text-sm text-navy-500">
                +{trialClients.length - 10} más usuarios de prueba
              </p>
            )}
          </div>
        )}
      </div>

      {/* Trial Vendors Section */}
      <div>
        <h3 className="text-lg font-semibold text-navy-900 mb-3 flex items-center gap-2">
          <Briefcase className="w-5 h-5 text-purple-500" />
          Vendedores en Modo Prueba
          <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full">
            {trialVendors.length}
          </span>
        </h3>
        
        {trialVendors.length === 0 ? (
          <div className="bg-gray-50 rounded-xl p-8 text-center">
            <Briefcase className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No hay vendedores en modo prueba</p>
          </div>
        ) : (
          <div className="space-y-3">
            {trialVendors.map((vendor) => (
              <motion.div
                key={vendor.id}
                initial={{ opacity: 0, y: 5 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white border border-purple-100 rounded-xl p-4 hover:shadow-md transition-shadow"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center text-purple-700 font-bold">
                      {vendor.name?.charAt(0).toUpperCase() || "?"}
                    </div>
                    <div>
                      <h4 className="font-semibold text-navy-900">{vendor.name}</h4>
                      <div className="flex items-center gap-3 text-sm text-navy-500">
                        <span className="flex items-center gap-1">
                          <Mail className="w-3 h-3" /> {vendor.email}
                        </span>
                        {vendor.phone && (
                          <span className="flex items-center gap-1">
                            <Phone className="w-3 h-3" /> {vendor.phone}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-navy-400 mt-1">
                        Registrado: {formatDate(vendor.created_at)}
                        {vendor.country && ` • ${vendor.country}`}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <ExpirationBadge expiresAt={vendor.trial_expires_at} isVendor={true} />
                    <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs font-medium rounded-full">
                      Demo
                    </span>
                    <Button 
                      size="sm"
                      variant="outline"
                      onClick={() => handleDeclineUser(vendor)}
                      className="border-red-200 text-red-600 hover:bg-red-50"
                      data-testid="decline-vendor-trial-btn"
                    >
                      <XCircle className="w-4 h-4" />
                    </Button>
                    <Button 
                      size="sm"
                      onClick={() => handleConvertVendor(vendor)}
                      className="bg-purple-600 hover:bg-purple-700"
                      data-testid="activate-vendor-btn"
                    >
                      <UserCheck className="w-4 h-4 mr-1" />
                      Activar
                    </Button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Convert Client Modal */}
      <ConvertTrialUserModal
        user={selectedUser}
        isOpen={showConvertModal}
        onClose={() => {
          setShowConvertModal(false);
          setSelectedUser(null);
        }}
        onUserConverted={() => {
          fetchTrialUsers();
          onRefresh && onRefresh();
        }}
      />

      {/* Create Vendor Trial Modal */}
      <CreateVendorTrialModal
        isOpen={showCreateVendorModal}
        onClose={() => setShowCreateVendorModal(false)}
        onCreated={() => {
          fetchTrialUsers();
          onRefresh && onRefresh();
        }}
      />

      {/* Decline Modal */}
      <DeclineTrialModal
        isOpen={showDeclineModal}
        onClose={() => {
          setShowDeclineModal(false);
          setDeclineUser(null);
        }}
        user={declineUser}
        onDeclined={() => {
          fetchTrialUsers();
          onRefresh && onRefresh();
        }}
      />
    </div>
  );
};

// Archived Users Tab Component
const ArchivedUsersTab = ({ onRefresh }) => {
  const [archivedUsers, setArchivedUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [restoringId, setRestoringId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    fetchArchivedUsers();
  }, []);

  const fetchArchivedUsers = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/admin/trials/archived`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setArchivedUsers(response.data.archived_users || []);
    } catch (error) {
      toast.error("Error al cargar usuarios archivados");
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = async (userId, userName) => {
    setRestoringId(userId);
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/admin/trials/${userId}/restore`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${userName} ha sido restaurado`);
      fetchArchivedUsers();
      onRefresh && onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al restaurar usuario");
    } finally {
      setRestoringId(null);
    }
  };

  const handlePermanentDelete = async (userId, userName) => {
    if (!window.confirm(`¿Estás seguro de eliminar PERMANENTEMENTE a ${userName}? Esta acción NO se puede deshacer.`)) {
      return;
    }
    
    setDeletingId(userId);
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/admin/trials/${userId}/permanent`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${userName} ha sido eliminado permanentemente`);
      fetchArchivedUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al eliminar usuario");
    } finally {
      setDeletingId(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString('es-MX', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-10 h-10 border-4 border-gray-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Stats */}
      <div className="bg-gray-100 border border-gray-200 rounded-xl p-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gray-200 rounded-lg flex items-center justify-center">
            <Archive className="w-5 h-5 text-gray-600" />
          </div>
          <div>
            <p className="text-2xl font-bold text-gray-700">{archivedUsers.length}</p>
            <p className="text-sm text-gray-600">Usuarios Archivados</p>
          </div>
        </div>
      </div>

      {archivedUsers.length === 0 ? (
        <div className="bg-white rounded-xl border border-navy-100 p-12 text-center">
          <Archive className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-navy-900 mb-2">Sin archivados</h3>
          <p className="text-navy-500">No hay usuarios archivados</p>
        </div>
      ) : (
        <div className="space-y-3">
          {archivedUsers.map((user) => {
            const isClient = user.original_data?.role === "client" || user.role === "client";
            return (
              <motion.div
                key={user.id}
                initial={{ opacity: 0, y: 5 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white border border-gray-200 rounded-xl p-4 hover:shadow-md transition-shadow"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold ${
                      isClient ? 'bg-amber-100 text-amber-700' : 'bg-purple-100 text-purple-700'
                    }`}>
                      {user.name?.charAt(0).toUpperCase() || "?"}
                    </div>
                    <div>
                      <h4 className="font-semibold text-navy-900">{user.name}</h4>
                      <p className="text-sm text-navy-500">{user.email}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                          isClient ? 'bg-amber-100 text-amber-700' : 'bg-purple-100 text-purple-700'
                        }`}>
                          {isClient ? 'Cliente' : 'Vendedor'}
                        </span>
                        <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs font-medium rounded-full">
                          {user.archive_reason_label || user.archive_reason}
                        </span>
                      </div>
                      {user.archive_observation && (
                        <p className="text-xs text-gray-500 mt-1 italic">
                          "{user.archive_observation}"
                        </p>
                      )}
                      <p className="text-xs text-gray-400 mt-1">
                        Archivado: {formatDate(user.archived_at)}
                        {user.archived_by_name && ` por ${user.archived_by_name}`}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button 
                      size="sm"
                      variant="outline"
                      onClick={() => handleRestore(user.id, user.name)}
                      disabled={restoringId === user.id}
                      className="border-emerald-200 text-emerald-600 hover:bg-emerald-50"
                      data-testid="restore-archived-btn"
                    >
                      {restoringId === user.id ? (
                        <RefreshCw className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <RotateCcw className="w-4 h-4 mr-1" />
                          Restaurar
                        </>
                      )}
                    </Button>
                    <Button 
                      size="sm"
                      variant="outline"
                      onClick={() => handlePermanentDelete(user.id, user.name)}
                      disabled={deletingId === user.id}
                      className="border-red-200 text-red-600 hover:bg-red-50"
                      data-testid="delete-permanent-btn"
                    >
                      {deletingId === user.id ? (
                        <RefreshCw className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <Trash2 className="w-4 h-4 mr-1" />
                          Eliminar
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Pending Payments Tab Component
const PendingPaymentsTab = ({ onRefresh }) => {
  const [pendingPayments, setPendingPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [approving, setApproving] = useState(null);

  useEffect(() => {
    fetchPendingPayments();
  }, []);

  const fetchPendingPayments = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/admin/pending-payments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPendingPayments(response.data.pending_payments || []);
    } catch (error) {
      console.error("Error fetching pending payments:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (clientId) => {
    setApproving(clientId);
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/admin/pending-payments/${clientId}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Pago aprobado correctamente");
      fetchPendingPayments();
      onRefresh && onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al aprobar pago");
    } finally {
      setApproving(null);
    }
  };

  const handleReject = async (clientId) => {
    if (!window.confirm("¿Estás seguro de rechazar este pago?")) return;
    
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/admin/pending-payments/${clientId}/reject`, {
        reason: "Pago no confirmado"
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Pago rechazado");
      fetchPendingPayments();
    } catch (error) {
      toast.error("Error al rechazar pago");
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString('es-MX', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-10 h-10 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Stats */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
            <CreditCard className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <p className="text-2xl font-bold text-blue-700">{pendingPayments.length}</p>
            <p className="text-sm text-blue-600">Pagos Pendientes de Aprobación</p>
          </div>
        </div>
      </div>

      {pendingPayments.length === 0 ? (
        <div className="bg-white rounded-xl border border-navy-100 p-12 text-center">
          <CheckCircle className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-navy-900 mb-2">Todo al día</h3>
          <p className="text-navy-500">No hay pagos pendientes de aprobación</p>
        </div>
      ) : (
        <div className="space-y-3">
          {pendingPayments.map((client) => (
            <motion.div
              key={client.id}
              initial={{ opacity: 0, y: 5 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white border border-blue-100 rounded-xl p-4"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-bold">
                    {client.name?.charAt(0).toUpperCase() || "?"}
                  </div>
                  <div>
                    <h4 className="font-semibold text-navy-900">{client.name}</h4>
                    <div className="flex items-center gap-3 text-sm text-navy-500">
                      <span className="flex items-center gap-1">
                        <Mail className="w-3 h-3" /> {client.email}
                      </span>
                      {client.phone && (
                        <span className="flex items-center gap-1">
                          <Phone className="w-3 h-3" /> {client.phone}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs font-medium rounded-full">
                        {client.plan_type?.toUpperCase() || "ESTÁNDAR"}
                      </span>
                      {client.plan_price && (
                        <span className="text-xs text-navy-500">
                          ${client.plan_price}
                        </span>
                      )}
                      <span className="text-xs text-navy-400">
                        • {formatDate(client.created_at)}
                      </span>
                    </div>
                    {client.vendedor_name && (
                      <p className="text-xs text-navy-400 mt-1">
                        Vendedor: {client.vendedor_name}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button 
                    size="sm"
                    variant="outline"
                    onClick={() => handleReject(client.id)}
                    className="border-red-200 text-red-600 hover:bg-red-50"
                  >
                    <XCircle className="w-4 h-4 mr-1" />
                    Rechazar
                  </Button>
                  <Button 
                    size="sm"
                    onClick={() => handleApprove(client.id)}
                    disabled={approving === client.id}
                    className="bg-emerald-600 hover:bg-emerald-700"
                  >
                    {approving === client.id ? (
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-1" />
                    ) : (
                      <CheckCircle className="w-4 h-4 mr-1" />
                    )}
                    Aprobar
                  </Button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

// Main Component
const AdminApprovals = () => {
  const [activeTab, setActiveTab] = useState("trial");
  const [refreshKey, setRefreshKey] = useState(0);

  const handleRefresh = () => {
    setRefreshKey(prev => prev + 1);
  };

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-heading text-3xl font-bold text-navy-900 flex items-center gap-3">
              <Clock className="w-8 h-8 text-amber-500" />
              Centro de Aprobaciones
            </h1>
            <p className="text-navy-500">Gestiona usuarios en prueba, archivados y pagos pendientes</p>
          </div>
          <Button onClick={handleRefresh} variant="outline">
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualizar
          </Button>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-3 mb-6">
            <TabsTrigger 
              value="trial" 
              data-testid="tab-trial-users"
              className="flex items-center gap-2 data-[state=active]:bg-amber-100 data-[state=active]:text-amber-700"
            >
              <Sparkles className="w-4 h-4" />
              Usuarios en Prueba
            </TabsTrigger>
            <TabsTrigger 
              value="archived"
              data-testid="tab-archived-users"
              className="flex items-center gap-2 data-[state=active]:bg-gray-200 data-[state=active]:text-gray-700"
            >
              <Archive className="w-4 h-4" />
              Archivados
            </TabsTrigger>
            <TabsTrigger 
              value="payments"
              data-testid="tab-pending-payments"
              className="flex items-center gap-2 data-[state=active]:bg-blue-100 data-[state=active]:text-blue-700"
            >
              <CreditCard className="w-4 h-4" />
              Pagos Pendientes
            </TabsTrigger>
          </TabsList>

          <TabsContent value="trial">
            <TrialUsersTab key={`trial-${refreshKey}`} onRefresh={handleRefresh} />
          </TabsContent>

          <TabsContent value="archived">
            <ArchivedUsersTab key={`archived-${refreshKey}`} onRefresh={handleRefresh} />
          </TabsContent>

          <TabsContent value="payments">
            <PendingPaymentsTab key={`payments-${refreshKey}`} onRefresh={handleRefresh} />
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
};

export default AdminApprovals;
